import { Fragment, useState, useMemo, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { EmptyStateMessage, Section, SectionCard, SectionHeader, SubsectionHeader } from '@/components/common';
import { RichTextEditor, RichTextReadOnly } from '@/components/common/RichTextEditor';
import { stripHtml } from '@/utils/stripHtml';
import { SelfAssessmentRow } from './SelfAssessmentRow';
import { useUpdateSelfAssessment } from '@/queries/useAppraisalDetail';
import type { AppraisalDetailResponse, SelfAssessmentItem } from '@/api/types/appraisal.types';
import type { RecordAccess } from '@/auth/permissions.types';
import { readOnlySectionVariant } from '@/auth/sectionReadOnlyStyle';
import { alwaysSeesEmptySections } from '@/auth/sectionVisibility';

interface SelfAssessmentSectionProps {
  appraisalDetail: AppraisalDetailResponse;
  permissions: RecordAccess;
  validationTriggered?: boolean;
}

const MIN_ITEMS = 3;
const MAX_ITEMS = 6;

function createEmptyItem(orderIndex: number): SelfAssessmentItem {
  return {
    developmentNeed: '',
    appraiseeResponse: '',
    orderIndex,
  };
}

function ensureMinItems(items: SelfAssessmentItem[]): SelfAssessmentItem[] {
  const result = [...items];
  while (result.length < MIN_ITEMS) {
    result.push(createEmptyItem(result.length));
  }
  return result;
}

function padToFlpCount(items: SelfAssessmentItem[], flpCount: number): SelfAssessmentItem[] {
  if (items.length >= flpCount) return items;
  const padded = [...items];
  while (padded.length < flpCount) padded.push(createEmptyItem(padded.length));
  return padded;
}

export function SelfAssessmentSection({
  appraisalDetail,
  permissions,
  validationTriggered = false,
}: SelfAssessmentSectionProps) {
  const sectionAccess = permissions.sections.selfAssessment;
  const canEdit = sectionAccess?.access === 'edit';
  const showValidation = validationTriggered && canEdit;
  const readOnlyVariant = readOnlySectionVariant('selfAssessment', permissions.role);

  const previousFLP = appraisalDetail.previousForwardLookingPlan;
  const isModeA = (previousFLP?.length ?? 0) > 0;

  const updateMutation = useUpdateSelfAssessment(String(appraisalDetail.id));

  const serverItems = appraisalDetail.selfAssessmentItems;
  const serverAdditionalContext = appraisalDetail.selfAssessmentAdditionalContext ?? '';
  const serverKey = useMemo(
    () => JSON.stringify({ items: serverItems ?? [], ctx: serverAdditionalContext }),
    [serverItems, serverAdditionalContext]
  );

  const [localItems, setLocalItems] = useState<SelfAssessmentItem[]>(() => {
    const items = serverItems ?? [];
    return isModeA ? padToFlpCount(items, previousFLP?.length ?? 0) : ensureMinItems(items);
  });

  const [localAdditionalContext, setLocalAdditionalContext] = useState(serverAdditionalContext);

  // Reset local state when server data changes (after mutation success / cache invalidation)
  const [prevKey, setPrevKey] = useState(serverKey);
  if (serverKey !== prevKey) {
    setPrevKey(serverKey);
    const items = serverItems ?? [];
    setLocalItems(isModeA ? padToFlpCount(items, previousFLP?.length ?? 0) : ensureMinItems(items));
    setLocalAdditionalContext(serverAdditionalContext);
  }

  // Prevents blur-triggered save from racing with add/delete-triggered save.
  // When clicking ADD/DELETE while a field has focus, blur fires first (same rowVersion),
  // then the click handler fires — causing a duplicate request and 409 conflict.
  const actionPendingRef = useRef(false);

  const handleChange = (index: number, field: 'developmentNeed' | 'appraiseeResponse', value: string) => {
    const newItems = [...localItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setLocalItems(newItems);
  };

  const handleBlur = () => {
    if (actionPendingRef.current) {
      actionPendingRef.current = false;
      return;
    }
    updateMutation.mutate({
      items: localItems,
      additionalContext: localAdditionalContext,
      rowVersion: appraisalDetail.rowVersion,
    });
  };

  const handleAdditionalContextChange = (value: string) => {
    setLocalAdditionalContext(value);
  };

  const handleAdditionalContextBlur = () => {
    if (actionPendingRef.current) {
      actionPendingRef.current = false;
      return;
    }
    updateMutation.mutate({
      items: localItems,
      additionalContext: localAdditionalContext,
      rowVersion: appraisalDetail.rowVersion,
    });
  };

  const handleActionMouseDown = () => {
    actionPendingRef.current = true;
  };

  const handleAdd = () => {
    const newItems = [...localItems, createEmptyItem(localItems.length)];
    setLocalItems(newItems);
    updateMutation.mutate({
      items: newItems,
      additionalContext: localAdditionalContext,
      rowVersion: appraisalDetail.rowVersion,
    });
  };

  const handleDelete = (index: number) => {
    if (localItems.length <= MIN_ITEMS) return;

    const newItems = localItems
      .filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, orderIndex: i }));

    setLocalItems(newItems);
    updateMutation.mutate({
      items: newItems,
      additionalContext: localAdditionalContext,
      rowVersion: appraisalDetail.rowVersion,
    });
  };

  const canDelete = localItems.length > MIN_ITEMS;
  const canAdd = localItems.length < MAX_ITEMS;

  // In read-only mode, filter out empty rows (in Mode A, also check previous FLP data)
  const itemsToDisplay = canEdit
    ? localItems
    : localItems.filter((item) => {
        if (isModeA) {
          const prevItem = previousFLP?.[item.orderIndex];
          return (
            stripHtml(prevItem?.developmentNeed ?? '') !== '' ||
            stripHtml(prevItem?.actionToResolve ?? '') !== '' ||
            stripHtml(item.appraiseeResponse) !== ''
          );
        }
        return stripHtml(item.developmentNeed) !== '' || stripHtml(item.appraiseeResponse) !== '';
      });

  const hasResponseError = showValidation && localItems.some(
    (item) => stripHtml(item.appraiseeResponse) === ''
  );

  if (
    !canEdit &&
    itemsToDisplay.length === 0 &&
    stripHtml(localAdditionalContext) === '' &&
    !alwaysSeesEmptySections(permissions.role)
  ) {
    return null;
  }

  return (
    <Section>
      <SectionHeader error={hasResponseError}>SELF-ASSESSMENT</SectionHeader>

      {isModeA ? (
        // Mode A: previous FLP items with response column
        <SectionCard error={hasResponseError}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>
            Progress against identified development needs
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <SubsectionHeader>DEVELOPMENT NEEDS FROM PREVIOUS REVIEW</SubsectionHeader>
            </Box>
            <Box sx={{ flex: 1 }}>
              <SubsectionHeader>APPRAISEE RESPONSE</SubsectionHeader>
            </Box>
          </Box>

          {itemsToDisplay.length === 0 ? (
            <EmptyStateMessage message="No self assessment items recorded." />
          ) : (
            itemsToDisplay.map((item, index) => {
              const prevItem = previousFLP?.[item.orderIndex];
              return (
                <Fragment key={item.id ?? `new-${index}`}>
                  <SelfAssessmentRow
                    mode="A"
                    developmentNeed={prevItem?.developmentNeed ?? ''}
                    actionToResolve={prevItem?.actionToResolve ?? ''}
                    appraiseeResponse={item.appraiseeResponse}
                    onChange={(field, value) => handleChange(index, field, value)}
                    onBlur={handleBlur}
                    onDelete={() => handleDelete(index)}
                    onDeleteMouseDown={handleActionMouseDown}
                    canEdit={canEdit}
                    canDelete={false}
                    readOnlyVariant={readOnlyVariant}
                    striped={index % 2 === 0}
                    error={{
                      appraiseeResponse:
                        showValidation && stripHtml(item.appraiseeResponse) === '',
                    }}
                  />
                </Fragment>
              );
            })
          )}

          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Additional Context for Collator / Key Areas to Test
            </Typography>
            {canEdit ? (
              <RichTextEditor
                content={localAdditionalContext}
                onChange={handleAdditionalContextChange}
                onBlur={handleAdditionalContextBlur}
                placeholder="Enter additional context..."
              />
            ) : (
              <RichTextReadOnly content={localAdditionalContext} variant={readOnlyVariant} />
            )}
          </Box>
        </SectionCard>
      ) : (
        // Mode B: two editable columns with add/delete
        <SectionCard error={hasResponseError}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Development needs and responses
            </Typography>
            {canEdit && canAdd && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onMouseDown={handleActionMouseDown}
                onClick={handleAdd}
                size="small"
              >
                ADD DEVELOPMENT NEED
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <SubsectionHeader>DEVELOPMENT NEED</SubsectionHeader>
            </Box>
            <Box sx={{ flex: 1 }}>
              <SubsectionHeader>APPRAISEE RESPONSE</SubsectionHeader>
            </Box>
            {canEdit && <Box sx={{ width: 40 }} />}
          </Box>

          {itemsToDisplay.length === 0 ? (
            <EmptyStateMessage message="No self assessment items recorded." />
          ) : (
            itemsToDisplay.map((item, index) => (
              <SelfAssessmentRow
                key={item.id ?? `new-${index}`}
                mode="B"
                developmentNeed={item.developmentNeed}
                actionToResolve=""
                appraiseeResponse={item.appraiseeResponse}
                onChange={(field, value) => handleChange(index, field, value)}
                onBlur={handleBlur}
                onDelete={() => handleDelete(index)}
                onDeleteMouseDown={handleActionMouseDown}
                canEdit={canEdit}
                canDelete={canDelete}
                readOnlyVariant={readOnlyVariant}
                striped={index % 2 === 0}
                error={{
                  appraiseeResponse:
                    showValidation && stripHtml(item.appraiseeResponse) === '',
                }}
              />
            ))
          )}

          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Additional Context for Collator / Key Areas to Test
            </Typography>
            {canEdit ? (
              <RichTextEditor
                content={localAdditionalContext}
                onChange={handleAdditionalContextChange}
                onBlur={handleAdditionalContextBlur}
                placeholder="Enter additional context..."
              />
            ) : (
              <RichTextReadOnly content={localAdditionalContext} variant={readOnlyVariant} />
            )}
          </Box>
        </SectionCard>
      )}
    </Section>
  );
}

export default SelfAssessmentSection;
